import {
	AreaDTO,
	EnumOptionDTO,
	Paginated,
	StaffDetailDTO,
	SuccessResponse,
	SystemDictTreeDTO,
	UploadFormRequest,
	rolesListRes,
} from './DTO/common.interface';
import {
	LogHistoryDTO,
	LogHistoryFilter,
} from './DTO/pre-admission/preAdmission.interface';
import { FamilyCarersDTO } from './DTO/member-management/admissionRequest';
import { apiSlice } from './apiSlice';
import fileDownload from 'js-file-download';
import {
	TypeOfQueryMemberStatusListEnum,
	TypeOfQueryServiceFollowListEnum,
} from './enum/common.enum';

const safeDecode = (value: string) => {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
};

// Prefer Content-Disposition (filename* > filename); fall back to the URL's last path segment.
const getDownloadFileName = (response: Response) => {
	const disposition = response.headers.get('Content-Disposition') || '';
	const encoded = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(
		disposition
	)?.[1];
	if (encoded) return safeDecode(encoded.trim().replace(/^"|"$/g, ''));
	const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition)?.[1];
	if (plain) return plain.trim();
	return safeDecode(new URL(response.url).pathname.split('/').pop() || '');
};

export const commonApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getRoles: builder.query<SuccessResponse<rolesListRes[]>, null>({
			query: () => ({
				url: `/web/roles/staff`,
			}),
		}),

		uploadMedia: builder.mutation<
			SuccessResponse<{ fileKey: string; url: string; name: string }[]>,
			FormData
		>({
			query: (body) => ({ url: `/web/media/upload`, method: 'POST', body }),
		}),

		getDicList: builder.query<SuccessResponse<any>, string>({
			query: (type) => ({
				url: `/web/dict/list/${type}`,
			}),
		}),
		getAreaItemList: builder.query<
			SuccessResponse<any>,
			{ type: number; parentId?: string }
		>({
			query: (body) => ({
				url: `/web/area/list/by-params`,
				method: 'POST',
				body,
			}),
		}),
		getAreaSubItemList: builder.mutation<SuccessResponse<AreaDTO[]>, string>({
			query: (parentId) => ({
				url: `/web/area/list/${parentId}`,
				method: 'GET',
			}),
		}),
		getListByParams: builder.query<
			SuccessResponse<StaffDetailDTO>,
			{ branchId: string; roleType: number }
		>({
			query: (body) => ({
				url: `/web/staffs/list/by-params`,
				method: 'POST',
				body,
			}),
		}),
		getDicTreeList: builder.mutation<
			SuccessResponse<SystemDictTreeDTO[]>,
			string
		>({
			query: (id) => ({
				// url: `/web/dict/tree/${body.type}/${body.code}`,
				url: `/web/dict/referral-agency/tree/${id}`,
				method: 'GET',
			}),
		}),
		getStaffAllList: builder.query<
			SuccessResponse<StaffDetailDTO[]>,
			{ branchId: string }
		>({
			query: (branchId) => ({
				url: `/web/staffs/get-by-branch/${branchId}`,
				method: 'GET',
			}),
		}),
		getAllEnumList: builder.query<SuccessResponse<any>, null>({
			query: () => ({
				url: `/web/enum/all`,
				method: 'GET',
			}),
		}),
		getQueryServiceFollowList: builder.query<
			SuccessResponse<EnumOptionDTO[]>,
			TypeOfQueryServiceFollowListEnum
		>({
			query: (type) => ({
				url: `/web/enum/${type}/service-follow`,
				method: 'GET',
			}),
		}),
		getQueryMemberStatusList: builder.query<
			SuccessResponse<EnumOptionDTO[]>,
			TypeOfQueryMemberStatusListEnum
		>({
			query: (type) => ({
				url: `/web/enum/${type}/member-status`,
				method: 'GET',
			}),
		}),
		getDiagnosisTreeList: builder.query<SuccessResponse<any>, null>({
			query: () => ({
				url: `/web/dict/diagnosis/tree`,
				method: 'GET',
			}),
		}),
		getLogHistoryList: builder.query<
			SuccessResponse<Paginated<LogHistoryDTO>>,
			LogHistoryFilter
		>({
			query: (body) => ({
				url: `/web/form/log-history/page`,
				method: 'POST',
				body,
			}),
		}),
		uploadForm: builder.mutation<SuccessResponse<any>, FormData>({
			query: (body) => ({
				url: `/web/form/upload-file/upload`,
				method: 'POST',
				body,
			}),
			invalidatesTags: [
				'EnquiryRecordList',
				'FaceSheetList',
				'Appendix4List',
				'Appendix5List',
				'Appendix6List',
				'Appendix7List',
				'MemberAppcalitonList',
				'NewConsentList',
				'StopServiceList',
				'WellBeingScaleList',
				'RecoveryAssessmentScaleList',
				'Dsm5AdultList',
				'Dsm5TeenList',
				'UploadFormList',
				'FamilyFormList',
				'FamilyFormFormList',
			],
		}),
		deleteUploadForm: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/form/upload-file/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: [
				'EnquiryRecordList',
				'FaceSheetList',
				'Appendix4List',
				'Appendix5List',
				'Appendix6List',
				'Appendix7List',
				'MemberAppcalitonList',
				'NewConsentList',
				'StopServiceList',
				'WellBeingScaleList',
				'RecoveryAssessmentScaleList',
				'Dsm5AdultList',
				'Dsm5TeenList',
				'UploadFormList',
				'FamilyFormFormList',
			],
		}),
		getUnitList: builder.query<SuccessResponse<any>, null>({
			query: () => ({
				url: `/web/branch/list`,
				method: 'GET',
			}),
		}),
		changeBranch: builder.mutation<SuccessResponse<any>, { branchId: string }>({
			query: (body) => ({
				url: `/web/staffs/switch-branch`,
				method: 'POST',
				body,
				credentials: 'include',
			}),
		}),
		getUploadFormList: builder.query<
			SuccessResponse<any>,
			{ id: string; type: any }
		>({
			query: (body) => ({
				url: `/web/form/upload-file/list/${body.id}/${body.type}`,
				method: 'GET',
			}),
			providesTags: ['UploadFormList'],
		}),

		getExportCustomerPDF: builder.mutation<Blob, string>({
			query(url) {
				return {
					url: url,
					method: 'GET',
					responseHandler: async (response) => {
						fileDownload(await response.blob(), getDownloadFileName(response));
					},
					cache: 'no-cache',
				};
			},
		}),
		getFaceSheetGenerateReport: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/form/face-sheet/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
		getEnquiryRecordGenerateReport: builder.mutation<
			SuccessResponse<any>,
			string
		>({
			query: (id) => ({
				url: `/web/form/enquiry-record/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
		getAppendix4GenerateReport: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/form/appendix-four/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
		getAppendix5GenerateReport: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/form/appendix-five/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
		getAppendix6GenerateReport: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/form/appendix-six/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
		getMemberApplicationGenerateReport: builder.mutation<
			SuccessResponse<any>,
			string
		>({
			query: (id) => ({
				url: `/web/form/member-application/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
		getConsentPersonalDataUsageGenerateReport: builder.mutation<
			SuccessResponse<any>,
			string
		>({
			query: (id) => ({
				url: `/web/form/consent-personal-data-usage/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
		getConsentServiceTerminationGenerateReport: builder.mutation<
			SuccessResponse<any>,
			string
		>({
			query: (id) => ({
				url: `/web/form/consent-service-termination/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
		getAppendix7GenerateReport: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/form/appendix-seven/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
		getWellBeingScaleGenerateReport: builder.mutation<
			SuccessResponse<any>,
			string
		>({
			query: (id) => ({
				url: `/web/form/well-being-scale/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
		getRecoveryAssessmentScaleGenerateReport: builder.mutation<
			SuccessResponse<any>,
			string
		>({
			query: (id) => ({
				url: `/web/form/recovery-assessment-scale/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
		getDsmFiveAdultGenerateReport: builder.mutation<
			SuccessResponse<any>,
			string
		>({
			query: (id) => ({
				url: `/web/form/dsm-five-adult/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
		getDsmFiveTeenGenerateReport: builder.mutation<
			SuccessResponse<any>,
			string
		>({
			query: (id) => ({
				url: `/web/form/dsm-five-teen/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
		getFamilyCarersMemberList: builder.query<
			SuccessResponse<FamilyCarersDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/family-carers/${id}/list`,
				method: 'GET',
				credentials: 'include',
			}),
			providesTags: ['FamilyCarersList'],
		}),
		getDictByPcode: builder.query<SuccessResponse<any>, any>({
			query: (body) => ({
				url: `/web/dict/list-by-parent-id/${body.type}/${body.pid}`,
				method: 'GET',
				credentials: 'include',
			}),
		}),
		getFamilyGenerateReport: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/form/family-member-application/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
		getInterventionGenerateReport: builder.mutation<
			SuccessResponse<any>,
			string
		>({
			query: (id) => ({
				url: `/web/intervention/${id}/generate-report`,
				method: 'POST',
				credentials: 'include',
			}),
		}),
	}),
});

export const {
	useGetRolesQuery,
	useUploadMediaMutation,
	useGetDicListQuery,
	useGetAreaItemListQuery,
	useGetAreaSubItemListMutation,
	useGetListByParamsQuery,
	useGetDicTreeListMutation,
	useGetStaffAllListQuery,
	useGetAllEnumListQuery,
	useGetQueryServiceFollowListQuery,
	useGetQueryMemberStatusListQuery,
	useGetDiagnosisTreeListQuery,
	useGetLogHistoryListQuery,
	useUploadFormMutation,
	useDeleteUploadFormMutation,
	useGetUnitListQuery,
	useChangeBranchMutation,
	useGetUploadFormListQuery,
	useGetExportCustomerPDFMutation,
	useGetFaceSheetGenerateReportMutation,
	useGetEnquiryRecordGenerateReportMutation,
	useGetAppendix4GenerateReportMutation,
	useGetAppendix5GenerateReportMutation,
	useGetAppendix6GenerateReportMutation,
	useGetMemberApplicationGenerateReportMutation,
	useGetConsentPersonalDataUsageGenerateReportMutation,
	useGetConsentServiceTerminationGenerateReportMutation,
	useGetAppendix7GenerateReportMutation,
	useGetWellBeingScaleGenerateReportMutation,
	useGetRecoveryAssessmentScaleGenerateReportMutation,
	useGetDsmFiveAdultGenerateReportMutation,
	useGetDsmFiveTeenGenerateReportMutation,
	useGetFamilyCarersMemberListQuery,
	useGetDictByPcodeQuery,
	useGetFamilyGenerateReportMutation,
	useGetInterventionGenerateReportMutation,
} = commonApiSlice;
